import { useState } from 'react'
import { type Employee, shortName } from '@/entities/employee'
import {
  type BlockReason,
  REASON_LABELS,
  REASONS,
  type TimeBlock,
  useMoveTimeBlockMutation,
  useOccupyTimeMutation,
  useReleaseTimeMutation,
} from '@/entities/time-block'
import { errorMessage, type Warning } from '@/shared/api'
import type { Interval } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input } from '@/shared/ui/field'
import { IntervalField } from '@/shared/ui/interval-field'
import { Segmented } from '@/shared/ui/segmented'
import { Select } from '@/shared/ui/select'
import { toast } from '@/shared/ui/toast'

export interface TimeBlockDialogState {
  /** Правим существующий блок либо закрываем время заново. */
  block: TimeBlock | null
  slot: Interval
  employeeId: string | null
}

interface TimeBlockDialogProps {
  state: TimeBlockDialogState | null
  employees: Employee[]
  /** Кого подставить по умолчанию, когда выбирать не из чего (сотрудник видит только себя). */
  selfId: string | null
  onClose: () => void
}

function warningText(warnings: Warning[], employees: Employee[]): string | null {
  if (warnings.length === 0) return null
  const names = [...new Set(warnings.map((w) => w.employeeId))]
    .map((id) => employees.find((e) => e.id === id)?.name ?? 'кто-то')
    .join(', ')
  const what = warnings.map((w) => w.title || w.reason || 'занято').join(', ')
  return `В это время у ${names} уже стоит: ${what}`
}

/**
 * Карточка занятого времени: выходной, отпуск, болезнь или «занят». Ни названия,
 * ни отметки выполнения здесь нет — блок ничего не поручает, а только закрывает
 * окно в расписании, поэтому в список дел он не попадает.
 *
 * Наложение на выход не запрещено: заболевший не обязан сначала снимать с себя
 * экскурсию. Пересечение приходит предупреждением после сохранения.
 */
export function TimeBlockDialog({ state, employees, selfId, onClose }: TimeBlockDialogProps) {
  const occupy = useOccupyTimeMutation()
  const move = useMoveTimeBlockMutation()
  const release = useReleaseTimeMutation()

  const block = state?.block ?? null
  const [employeeId, setEmployeeId] = useState<string | null>(
    block?.employeeId ?? state?.employeeId ?? selfId,
  )
  const [slot, setSlot] = useState<Interval | null>(block?.slot ?? state?.slot ?? null)
  const [reason, setReason] = useState<BlockReason>(block?.reason ?? 'day-off')
  const [note, setNote] = useState(block?.note ?? '')
  const [key, setKey] = useState('')

  // Диалог переиспользуется между блоками — при смене цели поля перезаполняются
  // из новой, а не тянут прежний ввод.
  const nextKey = `${block?.id ?? 'new'}:${state?.slot.startsAt.toISOString() ?? ''}`
  if (state && key !== nextKey) {
    setKey(nextKey)
    setEmployeeId(state.block?.employeeId ?? state.employeeId ?? selfId)
    setSlot(state.block?.slot ?? state.slot)
    setReason(state.block?.reason ?? 'day-off')
    setNote(state.block?.note ?? '')
  }

  const report = (warnings: Warning[]) => {
    const text = warningText(warnings, employees)
    if (text) toast.notify(text)
    onClose()
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!slot) return
    const trimmed = note.trim()
    if (block) {
      move.mutate(
        { id: block.id, patch: { slot, reason, note: trimmed } },
        { onSuccess: (result) => report(result.warnings) },
      )
      return
    }
    occupy.mutate(
      { employeeId, slot, reason, note: trimmed },
      { onSuccess: (result) => report(result.warnings) },
    )
  }

  const pending = occupy.isPending || move.isPending
  const error = occupy.error ?? move.error ?? release.error
  // Своя причина без подписи не отвечает, почему время занято, — бэкенд её и не примет.
  const incomplete = reason === 'other' && note.trim().length === 0

  return (
    <Dialog
      open={state !== null}
      onClose={onClose}
      title={block ? 'Занятое время' : 'Занять время'}
      size="lg"
      footer={
        <>
          {block && (
            <Button
              variant="danger"
              onClick={() => release.mutate(block.id, { onSuccess: onClose })}
              disabled={release.isPending}
            >
              Освободить
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            type="submit"
            form="time-block-form"
            disabled={pending || incomplete || employeeId === null}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <form id="time-block-form" onSubmit={submit} className="flex flex-col gap-5">
        {/* Владелец у блока ровно один и после заведения не меняется: перевести
            занятое время на другого — это освободить и закрыть заново. */}
        {block ? (
          <Field label="У кого">
            {(props) => (
              <Input
                {...props}
                readOnly
                value={
                  employees.find((e) => e.id === block.employeeId)?.name ?? 'сотрудник не в команде'
                }
              />
            )}
          </Field>
        ) : (
          <Field label="У кого">
            {(props) => (
              <Select
                {...props}
                value={employeeId ?? ''}
                onChange={(value) => setEmployeeId(value || null)}
                placeholder="Выберите человека"
                options={employees.map((employee) => ({
                  value: employee.id,
                  label: shortName(employee),
                }))}
              />
            )}
          </Field>
        )}

        {slot && <IntervalField value={slot} onChange={setSlot} />}

        <div className="flex flex-col gap-2">
          <span className="label">Почему</span>
          <Segmented
            ariaLabel="Причина"
            value={reason}
            onChange={setReason}
            options={REASONS.map((value) => ({ value, label: REASON_LABELS[value] }))}
            className="flex-wrap"
          />
        </div>

        <Field label={reason === 'other' ? 'Своими словами' : 'Уточнение'}>
          {(props) => (
            <Input
              {...props}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={reason === 'other' ? 'уехал за реквизитом' : 'необязательно'}
            />
          )}
        </Field>

        {error && (
          <p role="alert" className="text-sm text-accent">
            {errorMessage(error)}
          </p>
        )}
      </form>
    </Dialog>
  )
}
